<template>
  <div class="image-homework-creator">
    <el-dialog
      :title="imageHomeworkForm.isEditing ? '编辑图片作业' : '自建图片作业'"
      :visible.sync="dialogVisible"
      width="60%"
      :before-close="handleClose"
    >
      <el-form :model="imageHomeworkForm" label-width="100px" ref="imageHomeworkForm" :rules="formRules">
        <el-form-item label="作业名称" prop="name">
          <el-input
            v-model="imageHomeworkForm.name"
            placeholder="请输入作业名称"
            @keyup.enter="submitForm"
          ></el-input>
        </el-form-item>

        <el-form-item label="科目" prop="subject">
          <el-select v-model="imageHomeworkForm.subject" placeholder="请选择科目" style="width: 100%">
            <el-option label="数学" value="数学"></el-option>
            <el-option label="语文" value="语文"></el-option>
            <el-option label="英语" value="英语"></el-option>
            <el-option label="物理" value="物理"></el-option>
            <el-option label="化学" value="化学"></el-option>
            <el-option label="生物" value="生物"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="选择班级" prop="selectedClassIds">
          <el-select
            v-model="imageHomeworkForm.selectedClassIds"
            multiple
            filterable
            placeholder="请选择班级"
            style="width: 100%"
          >
            <el-option
              v-for="item in classList"
              :key="item.id"
              :label="item.gradeName"
              :value="item.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="截止日期" prop="dueDate">
          <el-date-picker
            v-model="imageHomeworkForm.dueDate"
            type="datetime"
            placeholder="选择截止日期"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width: 100%">
          </el-date-picker>
        </el-form-item>

        <el-form-item label="说明" prop="description">
          <el-input type="textarea" :rows="3" v-model="imageHomeworkForm.description" placeholder="请输入作业说明"></el-input>
        </el-form-item>

        <el-form-item label="作业图片">
          <div class="upload-container">
            <el-upload
              action="#"
              :http-request="uploadImage"
              list-type="picture-card"
              :limit="10"
              :on-preview="handlePicturePreview"
              :on-remove="handleRemove"
              :on-exceed="handleExceed"
              :before-upload="beforeImageUpload"
              :file-list="fileList"
              :disabled="uploading"
              :auto-upload="true"
              multiple
            >
              <i class="el-icon-plus"></i>
              <div slot="tip" class="el-upload__tip">支持JPG、PNG格式，每张不超过5MB，最多上传10张</div>
            </el-upload>
          </div>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="cancelDialog">取 消</el-button>
        <el-button type="primary" @click="submitForm" :loading="uploading">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 图片预览弹窗 -->
    <el-dialog :visible.sync="previewVisible" append-to-body>
      <img width="100%" :src="previewUrl" alt="预览图片">
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { getToken } from '@/utils/auth'
import { createImageHomework } from '@/api/homework'
import { classPaging } from '@/api/class'

export default {
  name: 'ImageHomeworkCreator',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: false,
      uploading: false,
      fileList: [],
      uploadedUrls: [],
      imageHomeworkForm: {
        id: null, // 添加id字段，编辑时使用
        name: '',
        subject: '数学',
        selectedClassIds: [],
        dueDate: '',
        description: '',
        images: [],
        imageUrl: '', // 存储主图片URL
        additionalImages: [], // 存储额外图片URL
        isEditing: false // 标记是否处于编辑模式
      },
      classList: [],
      previewVisible: false,
      previewUrl: '',
      formRules: {
        name: [
          { required: true, message: '请输入作业名称', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        subject: [
          { required: true, message: '请选择科目', trigger: 'change' }
        ],
        selectedClassIds: [
          { required: true, message: '请选择班级', trigger: 'change' },
          { type: 'array', min: 1, message: '请至少选择一个班级', trigger: 'change' }
        ]
      }
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        // 如果不是编辑模式（即新建模式），重置表单
        if (!this.imageHomeworkForm.isEditing) {
          this.resetForm()
        }
        this.fetchClassList()
      }
    },
    dialogVisible(val) {
      if (!val) {
        this.$emit('update:visible', false)
      }
    }
  },
  methods: {
    // 获取班级列表
    async fetchClassList() {
      try {
        const res = await classPaging()
        console.log('班级列表原始数据:', res)

        if (res && res.data && res.data.records) {
          // 处理数据，确保每个班级对象都有gradeName属性
          this.classList = res.data.records.map(item => {
            // 如果没有gradeName或为空，则使用备用名称
            if (!item.gradeName || item.gradeName.trim() === '') {
              return {
                ...item,
                gradeName: item.name || `班级${item.id}` // 优先使用name字段，否则使用"班级+ID"
              }
            }
            return item
          })

          console.log('处理后的班级列表:', this.classList)
        } else {
          this.classList = []
          console.warn('未获取到班级数据或数据格式不正确')
        }
      } catch (error) {
        console.error('获取班级列表失败', error)
        this.$message.error('获取班级列表失败')
        this.classList = []
      }
    },

    // 图片上传前的验证
    beforeImageUpload(file) {
      const isValidFormat = file.type === 'image/jpeg' || file.type === 'image/png'
      const isLt5M = file.size / 1024 / 1024 < 5

      if (!isValidFormat) {
        this.$message.error('上传图片只能是 JPG 或 PNG 格式!')
        return false
      }
      if (!isLt5M) {
        this.$message.error('上传图片大小不能超过 5MB!')
        return false
      }
      return true
    },

    // 处理图片上传超出限制
    handleExceed() {
      this.$message.warning('最多只能上传10张图片')
    },

    // 处理图片预览
    handlePicturePreview(file) {
      this.previewUrl = file.url || URL.createObjectURL(file.raw)
      this.previewVisible = true
    },

    // 处理移除图片
    handleRemove(file, fileList) {
      this.fileList = fileList
      if (file.url && this.uploadedUrls.includes(file.url)) {
        const index = this.uploadedUrls.indexOf(file.url)
        if (index > -1) {
          this.uploadedUrls.splice(index, 1)
        }
      }
      this.imageHomeworkForm.images = this.uploadedUrls
    },

    // 自定义上传方法
    async uploadImage(options) {
      console.log('uploadImage 方法被调用，文件:', options.file)
      const file = options.file
      try {
        // 将文件添加到文件列表展示（本地预览）
        const existingFileIndex = this.fileList.findIndex(f => f.name === file.name)
        if (existingFileIndex >= 0) {
          // 如果文件已存在，替换它
          this.fileList.splice(existingFileIndex, 1)
        }

        // 添加到预览列表
        const fileItem = {
          name: file.name,
          url: URL.createObjectURL(file),
          raw: file
        }

        this.fileList.push(fileItem)

        // 同时更新表单中的images字段
        this.imageHomeworkForm.images = this.fileList.map(f => f.url)

        console.log('文件已添加到预览列表，当前文件列表:', this.fileList)
        // 注意：这里我们只是添加到预览列表，实际上传会在提交表单时进行

        // 手动触发表单验证
        this.$nextTick(() => {
          if (this.$refs.imageHomeworkForm) {
            // 验证整个表单
            this.$refs.imageHomeworkForm.validate(() => {})
          }
        })
      } catch (error) {
        console.error('添加文件到预览失败:', error)
        this.$message.error('添加文件失败: ' + (error.message || '未知错误'))
      }
    },

    // 上传图片到服务器
    async uploadImagesToServer() {
      // 获取需要上传的新图片（有raw属性的）
      const imagesToUpload = this.fileList.filter(file => file.raw)
      console.log('需要上传的新图片数量:', imagesToUpload.length)

      // 获取已存在的图片URL（没有raw属性，有isExisting标记的）
      const existingImages = this.fileList
        .filter(file => file.isExisting && !file.raw)
        .map(file => file.url)
      console.log('已存在的图片数量:', existingImages.length)

      // 如果没有任何图片（新上传的或已有的），返回错误
      if (imagesToUpload.length === 0 && existingImages.length === 0) {
        this.$message.error('没有可用的图片，请至少选择一张图片')
        return []
      }

      // 如果没有新上传的图片，但有已有的图片，直接返回已有图片URL
      if (imagesToUpload.length === 0 && existingImages.length > 0) {
        console.log('没有新图片需要上传，使用现有图片:', existingImages)
        return existingImages
      }

      const uploadResults = []
      this.uploading = true

      const loadingInstance = this.$loading({
        lock: true,
        text: '正在上传图片，请稍候...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })

      try {
        let completedCount = 0
        const totalCount = imagesToUpload.length

        for (const fileItem of imagesToUpload) {
          const formData = new FormData()
          formData.append('file', fileItem.raw)
          formData.append('bucketName', 'homework-images')

          loadingInstance.text = `正在上传第 ${completedCount + 1}/${totalCount} 张图片...`
          console.log(`开始上传图片: ${fileItem.name}, 大小: ${fileItem.raw.size} bytes, 类型: ${fileItem.raw.type}`)

          // 打印formData内容
          console.log('FormData内容:')
          for (let pair of formData.entries()) {
            if (pair[0] === 'file') {
              console.log(pair[0], '(文件对象)')
            } else {
              console.log(pair[0], pair[1])
            }
          }

          const response = await axios({
            url: '/api/teachcourse/uploadImage', // 使用相对路径
            method: 'post',
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': getToken()
            },
            onUploadProgress: progressEvent => {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              loadingInstance.text = `正在上传第 ${completedCount + 1}/${totalCount} 张图片: ${percent}%`
            }
          })

          console.log('上传响应状态:', response.status)
          console.log('上传响应完整数据:', response)

          // 处理响应
          if (response && response.data) {
            let imageUrl = null

            // 打印详细的响应数据结构
            console.log('响应数据类型:', typeof response.data)
            console.log('响应数据结构:', JSON.stringify(response.data))

            if (response.data.code === 1 && response.data.data) {
              imageUrl = response.data.data
              console.log('从code=1响应中提取URL:', imageUrl)
            } else if (response.data.code === 200 && response.data.data) {
              imageUrl = response.data.data
              console.log('从code=200响应中提取URL:', imageUrl)
            } else if (response.data.data) {
              imageUrl = response.data.data
              console.log('从data字段提取URL:', imageUrl)
            } else if (typeof response.data === 'string') {
              imageUrl = response.data
              console.log('从字符串响应中提取URL:', imageUrl)
            }

            if (!imageUrl) {
              console.error('无法从响应中提取图片URL')
              throw new Error(`上传图片 ${fileItem.name} 失败: 无法获取图片URL`)
            }

            // 检查返回的URL是否已经包含完整路径
            if (imageUrl && !imageUrl.startsWith('http')) {
              // 从后台接口获取MinIO服务器基础URL
              const baseUrl = process.env.VUE_APP_MINIO_URL || 'http://127.0.0.1:9000'
              imageUrl = `${baseUrl}${imageUrl}`
              console.log('拼接完整URL:', imageUrl)
            }

            console.log('图片上传成功，URL:', imageUrl)
            uploadResults.push(imageUrl)
            completedCount++
          } else {
            throw new Error(`上传图片 ${fileItem.name} 失败: 响应格式不正确`)
          }
        }

        loadingInstance.close()

        // 合并上传结果和已有图片
        const allImageUrls = [...uploadResults, ...existingImages]

        if (uploadResults.length === 0 && imagesToUpload.length > 0) {
          this.$message.error('所有新图片上传均失败')
        } else if (uploadResults.length < imagesToUpload.length) {
          this.$message.warning(`成功上传 ${uploadResults.length}/${imagesToUpload.length} 张新图片`)
        } else if (uploadResults.length > 0) {
          this.$message.success(`成功上传 ${uploadResults.length} 张新图片`)
        }

        if (allImageUrls.length === 0) {
          this.$message.error('没有可用图片，请确保至少有一张图片')
          return []
        }

        console.log('最终图片URL列表:', allImageUrls)
        this.uploadedUrls = allImageUrls
        return allImageUrls
      } catch (error) {
        console.error('上传图片异常:', error)
        loadingInstance.close()
        this.$message.error('上传图片失败: ' + (error.message || '网络错误'))
        throw error
      }
    },

    // 提交表单
    async submitForm() {
      // 添加调试日志
      console.log('点击了提交按钮，开始验证表单')
      console.log('表单引用:', this.$refs.imageHomeworkForm)
      console.log('当前表单数据:', this.imageHomeworkForm)
      console.log('文件列表:', this.fileList)

      if (!this.$refs.imageHomeworkForm) {
        this.$message.error('表单引用不存在，请刷新页面重试')
        return
      }

      // 单独验证文件列表
      if (this.fileList.length === 0) {
        this.$message.error('请至少上传一张作业图片')
        return
      }

      // 显示上传组件的内部详情
      console.log('上传组件文件列表详情:', JSON.stringify(this.fileList.map(f => ({
        name: f.name,
        size: f.size || (f.raw ? f.raw.size : 'unknown'),
        type: f.type || (f.raw ? f.raw.type : 'unknown')
      }))))

      // 开始表单验证
      this.$refs.imageHomeworkForm.validate(async (valid, validateFields) => {
        console.log('表单验证结果:', valid)

        if (!valid) {
          // 显示详细的验证错误信息
          console.log('验证错误字段:', validateFields)
          let errorMsg = '表单验证失败，请检查以下字段：'

          if (validateFields) {
            const fieldNames = {
              name: '作业名称',
              subject: '科目',
              selectedClassIds: '班级'
            }

            const errorFields = Object.keys(validateFields)
            errorFields.forEach(field => {
              errorMsg += `\n${fieldNames[field] || field}`
            })
          }

          this.$message.error(errorMsg)
          return false
        }

        console.log('表单验证通过，准备上传图片')
        this.uploading = true

        const loadingInstance = this.$loading({
          lock: true,
          text: '正在处理中，请稍候...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        })

        try {
          console.log('准备上传图片到服务器')
          // 检查文件列表中是否有原始文件数据
          const hasRawFiles = this.fileList.some(file => file.raw)
          console.log('文件列表中是否有原始文件:', hasRawFiles)

          if (!hasRawFiles) {
            this.$message.error('请重新选择图片进行上传')
            loadingInstance.close()
            this.uploading = false
            return
          }

          // 上传所有图片到服务器
          const imageUrls = await this.uploadImagesToServer()
          console.log('图片上传结果:', imageUrls)

          if (imageUrls.length === 0) {
            throw new Error('没有图片上传成功')
          }

          console.log('所有图片上传成功，开始为每个班级创建作业')

          // 创建作业结果数组
          const results = []
          const successClassIds = []
          const failedClassIds = []

          // 为每个班级创建作业
          for (const classId of this.imageHomeworkForm.selectedClassIds) {
            try {
              // 准备作业数据，区分编辑模式和新建模式
              const homeworkData = {
                name: this.imageHomeworkForm.name,
                subject: this.imageHomeworkForm.subject,
                dueDate: this.imageHomeworkForm.dueDate || new Date().toISOString().slice(0, 19).replace('T', ' '), // 如果没有设置，使用当前时间
                description: this.imageHomeworkForm.description,
                creator: this.$store.state.user.name || localStorage.getItem('username'),
                gradeId: Number(classId), // 确保是数字类型
                isImageType: true
              }

              // 添加班级名称字段
              const classItem = this.classList.find(item => item.id === classId)
              if (classItem && classItem.gradeName) {
                homeworkData.gradeName = classItem.gradeName
              }

              // 如果是编辑模式，并且有ID，添加ID字段
              if (this.imageHomeworkForm.isEditing && this.imageHomeworkForm.id) {
                homeworkData.id = this.imageHomeworkForm.id
              }

              // 处理图片URL
              if (imageUrls.length > 0) {
                // 有新上传的图片，使用新图片
                homeworkData.imageUrl = imageUrls[0]
                homeworkData.additionalImages = imageUrls.length > 1 ? imageUrls.slice(1) : []
              } else if (this.imageHomeworkForm.isEditing) {
                // 编辑模式且没有新图片，保留原图片
                homeworkData.imageUrl = this.imageHomeworkForm.imageUrl
                homeworkData.additionalImages = this.imageHomeworkForm.additionalImages || []
              } else {
                // 既没有新图片也不是编辑模式，这不应该发生
                throw new Error('没有可用的图片，请上传至少一张图片')
              }

              console.log(`创建班级ID=${classId}的作业:`, homeworkData)

              const response = await createImageHomework(homeworkData)
              console.log(`班级ID=${classId}创建作业响应:`, response)

              if (response && (response.code === 1 || response.code === 200)) {
                successClassIds.push(classId)
                results.push({
                  classId,
                  success: true,
                  data: response.data
                })
              } else {
                failedClassIds.push(classId)
                results.push({
                  classId,
                  success: false,
                  error: response?.msg || '未知错误'
                })
              }
            } catch (classError) {
              console.error(`班级ID=${classId}创建作业失败:`, classError)
              failedClassIds.push(classId)
              results.push({
                classId,
                success: false,
                error: classError.message || '未知错误'
              })
            }
          }

          loadingInstance.close()

          // 根据结果显示不同的提示信息
          if (successClassIds.length > 0 && failedClassIds.length === 0) {
            this.$message.success(`成功为${successClassIds.length}个班级创建图片作业`)
            this.resetForm()
            this.dialogVisible = false
            this.$emit('success', results) // 传递详细结果
          } else if (successClassIds.length > 0 && failedClassIds.length > 0) {
            this.$message.warning(`已为${successClassIds.length}个班级创建作业，但${failedClassIds.length}个班级失败`)
            // 不关闭对话框，让用户可以看到详细信息或重试
          } else {
            throw new Error('所有班级作业创建均失败')
          }
        } catch (error) {
          console.error('保存图片作业失败:', error)
          loadingInstance.close()
          this.$message.error('保存图片作业失败: ' + (error.message || '未知错误'))
        } finally {
          this.uploading = false
        }
      })
    },

    // 重置表单
    resetForm() {
      this.fileList = []
      this.uploadedUrls = []
      this.imageHomeworkForm = {
        id: null,
        name: '',
        subject: '数学',
        selectedClassIds: [],
        dueDate: '',
        description: '',
        images: [],
        imageUrl: '',
        additionalImages: [],
        isEditing: false
      }
      if (this.$refs.imageHomeworkForm) {
        this.$refs.imageHomeworkForm.resetFields()
      }
    },

    // 编辑作业方法
    async editHomework(homework) {
      console.log('编辑图片作业:', homework)

      try {
        // 清空当前状态
        this.resetForm()
        this.fileList = []

        // 设置编辑模式
        this.imageHomeworkForm = {
          id: homework.id,
          name: homework.name || '',
          subject: homework.subject || '数学',
          // 如果是编辑单个班级的作业，则只包含当前班级ID
          selectedClassIds: homework.gradeId ? [homework.gradeId] : [],
          dueDate: homework.dueDate || '',
          description: homework.description || '',
          images: [],
          imageUrl: homework.imageUrl || '',
          additionalImages: homework.additionalImages || [],
          isEditing: true
        }

        console.log('编辑表单数据设置完成:', this.imageHomeworkForm)

        // 如果有图片URL，添加到文件列表中以显示
        if (homework.imageUrl) {
          this.fileList.push({
            name: '主图片',
            url: homework.imageUrl,
            // 对于已有图片，不设置raw属性，表示这是已上传的图片
            isExisting: true
          })

          // 设置上传结果
          this.uploadedUrls = [homework.imageUrl]
        }

        // 如果有额外图片，也添加到文件列表
        if (homework.additionalImages && homework.additionalImages.length > 0) {
          homework.additionalImages.forEach((url, index) => {
            this.fileList.push({
              name: `额外图片${index + 1}`,
              url: url,
              isExisting: true
            })
            this.uploadedUrls.push(url)
          })
        }

        console.log('图片文件列表设置完成:', this.fileList)

        // 如果是编辑模式，更新标题
        this.$nextTick(() => {
          // 尝试更新对话框标题
          const dialogHeader = document.querySelector('.image-homework-creator .el-dialog__header .el-dialog__title')
          if (dialogHeader) {
            dialogHeader.textContent = '编辑图片作业'
          }
        })
      } catch (error) {
        console.error('设置编辑模式失败:', error)
        this.$message.error('初始化编辑失败: ' + error.message)
      }
    },

    // 取消对话框
    cancelDialog() {
      this.$confirm('确认关闭？未保存的内容将会丢失', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.resetForm()
        this.dialogVisible = false
      }).catch(() => {})
    },

    // 关闭对话框
    handleClose(done) {
      this.$confirm('确认关闭？未保存的内容将会丢失', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.resetForm()
        done()
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.image-homework-creator {
  width: 100%;
}

.upload-container {
  margin: 10px 0;
}

.el-upload__tip {
  line-height: 1.2;
  padding: 8px 0;
  color: #909399;
}

::v-deep .el-upload--picture-card {
  width: 120px;
  height: 120px;
  line-height: 120px;
}

::v-deep .el-upload-list--picture-card .el-upload-list__item {
  width: 120px;
  height: 120px;
}
</style>
